import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import type { Brand, BrandListResponse, User } from '../types/index.js';
import { apiGet } from './api.js';
import { apiPost } from './api-mutations.js';
import { useAuth } from './auth-context.js';

/**
 * Spec 076 rework (Pass 1.5), Spec 088c refactor.
 *
 *  - `/api/v1/me` no longer returns a tenantId (spec 076 decision #8). The
 *    old `synthUser` compat path was wrong on empty DBs: it set
 *    `user.tenantId = ''`, which caused settings pages to fire
 *    `/api/v1/tenants//brands` and 400.
 *
 *  - `activeTenantId` replaces `user.tenantId`. It is derived from the
 *    currently selected brand if there is one, otherwise from an explicitly
 *    bootstrapped tenant id (see `bootstrapTenant`).
 *
 *  - `bootstrapTenant()` is used by settings/admin pages to obtain a tenant
 *    id when no brand is selected yet (empty DB case). It lists the user's
 *    tenants, picks the first, or creates a default one on their behalf.
 *    Idempotent — subsequent calls return the cached value.
 *
 *  - Spec 088c: BrandProvider no longer fetches `/api/v1/me` independently.
 *    The user identity is owned by `AuthProvider` (see `auth-context.tsx`).
 *    This collapses the SPA to a single `/api/v1/me` request per session,
 *    which Gate 088c.D asserts.
 */
interface BrandContextValue {
  brands: Brand[];
  selectedBrand: Brand | null;
  selectedBrandTenantId: string | null;
  /**
   * Effective tenant id for settings/admin flows. Prefers the selected brand's
   * `tenant_id`. Null until a brand is selected OR `bootstrapTenant()` has
   * been awaited.
   */
  activeTenantId: string | null;
  /**
   * Ensures a tenant id is available. Safe to call repeatedly. Returns the
   * resolved tenant id. Throws on failure.
   */
  bootstrapTenant: () => Promise<string>;
  selectBrand: (brand: Brand) => void;
  refreshBrands: () => Promise<void>;
  loading: boolean;
  error: string | null;
  user: User | null;
}

const BrandContext = createContext<BrandContextValue | null>(null);

const STORAGE_KEY = 'geo_observatory_selected_brand_id';

interface TenantSummary {
  id: string;
  name: string;
}

interface TenantListResponse {
  data: TenantSummary[];
}

interface TenantCreateResponse {
  data: TenantSummary;
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const { user: authUser, isLoading: authLoading } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bootstrappedTenantId, setBootstrappedTenantId] = useState<string | null>(
    null,
  );
  // In-flight bootstrap promise so concurrent callers share the result.
  const bootstrapPromiseRef = useRef<Promise<string> | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Spec 088c: gate on AuthProvider completion so `/api/v1/me` fires once
    // (via AuthProvider) and `/api/v1/brands` fires exactly once afterward.
    if (authLoading) {
      return () => {
        cancelled = true;
      };
    }

    async function loadData() {
      try {
        // Spec 076: global brand list — server filters by the access predicate.
        const brandData = await apiGet<BrandListResponse>(
          '/api/v1/brands?limit=500&offset=0',
        );
        if (cancelled) return;
        setBrands(brandData.data);

        const savedBrandId = localStorage.getItem(STORAGE_KEY);
        const savedBrand = brandData.data.find((b) => b.id === savedBrandId);
        if (savedBrand) {
          setSelectedBrand(savedBrand);
        } else if (brandData.data.length > 0) {
          setSelectedBrand(brandData.data[0]);
          localStorage.setItem(STORAGE_KEY, brandData.data[0].id);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, [authLoading]);

  const selectBrand = useCallback((brand: Brand) => {
    setSelectedBrand(brand);
    localStorage.setItem(STORAGE_KEY, brand.id);
  }, []);

  const refreshBrands = useCallback(async () => {
    try {
      const brandData = await apiGet<BrandListResponse>(
        '/api/v1/brands?limit=500&offset=0',
      );
      setBrands(brandData.data);
      const currentId = selectedBrand?.id;
      if (currentId) {
        const updated = brandData.data.find((b) => b.id === currentId);
        if (updated) {
          setSelectedBrand(updated);
        } else if (brandData.data.length > 0) {
          setSelectedBrand(brandData.data[0]);
          localStorage.setItem(STORAGE_KEY, brandData.data[0].id);
        } else {
          setSelectedBrand(null);
          localStorage.removeItem(STORAGE_KEY);
        }
      } else if (brandData.data.length > 0) {
        setSelectedBrand(brandData.data[0]);
        localStorage.setItem(STORAGE_KEY, brandData.data[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh brands');
    }
  }, [selectedBrand?.id]);

  const selectedBrandTenantId = selectedBrand?.tenant_id ?? null;
  // Spec 152: resolve the effective tenant to one that ACTUALLY contains the
  // user's brands, never an arbitrary/empty first tenant. Precedence:
  //   1. the selected brand's tenant (brand-scoped flows),
  //   2. any loaded brand's tenant (settings tabs before a brand is picked, or
  //      when bootstrapTenant() would otherwise have raced to an empty tenant),
  //   3. the bootstrapped tenant (only reached when the user has NO brands —
  //      the genuine create-first-brand path).
  // This makes Settings → Brands target the brand-bearing tenant even when
  // bootstrapTenant() fired first and picked the empty "My Workspace" tenant,
  // and it re-scopes automatically when the selected brand changes.
  const activeTenantId =
    selectedBrandTenantId ?? brands[0]?.tenant_id ?? bootstrappedTenantId;

  const bootstrapTenant = useCallback(async (): Promise<string> => {
    // Fast paths: already have an effective tenant id.
    if (selectedBrand?.tenant_id) return selectedBrand.tenant_id;
    // Spec 152: if brands are loaded, the user's real tenant is whichever one
    // owns those brands — never the empty auto-created workspace. Resolve from
    // the loaded list before listing/creating tenants. Settles the empty-tenant
    // race at the source for any caller that awaits bootstrapTenant().
    if (brands.length > 0) {
      const tid = brands[0].tenant_id;
      setBootstrappedTenantId(tid);
      return tid;
    }
    if (bootstrappedTenantId) return bootstrappedTenantId;
    if (bootstrapPromiseRef.current) return bootstrapPromiseRef.current;

    const run = (async (): Promise<string> => {
      // 1. Try listing existing tenants. Be defensive: old builds returned
      //    a bare `null` body. Treat any non-array as "no tenants".
      let existing: TenantSummary[] = [];
      try {
        const listRaw = await apiGet<TenantListResponse | null>(
          '/api/v1/tenants',
        );
        if (listRaw && Array.isArray(listRaw.data)) {
          existing = listRaw.data;
        }
      } catch {
        // Non-fatal — fall through to create flow.
      }

      if (existing.length > 0) {
        // Spec 152: prefer a tenant that actually owns one of the user's loaded
        // brands over a blind `existing[0]` (which may be an empty
        // auto-created "My Workspace"). Falls back to the first tenant only
        // when none of the existing tenants own a brand (genuinely brand-less).
        const brandTenantIds = new Set(brands.map((b) => b.tenant_id));
        const brandBearing = existing.find((t) => brandTenantIds.has(t.id));
        const id = (brandBearing ?? existing[0]).id;
        setBootstrappedTenantId(id);
        return id;
      }

      // 2. No tenants — create a default one. Backend auto-adds the caller
      //    as owner.
      const displayName = authUser?.name || authUser?.email || 'My Workspace';
      const created = await apiPost<TenantCreateResponse>('/api/v1/tenants', {
        name: displayName,
        plan: 'starter',
      });
      const id = created.data.id;
      setBootstrappedTenantId(id);
      return id;
    })();

    bootstrapPromiseRef.current = run;
    try {
      const id = await run;
      return id;
    } finally {
      bootstrapPromiseRef.current = null;
    }
  }, [selectedBrand, bootstrappedTenantId, authUser, brands]);

  // Spec 088c: derive the legacy `User` shape from AuthProvider so existing
  // consumers (`useBrand().user`) keep working without changes. AuthProvider
  // returns a richer shape; we project onto the legacy interface.
  const user: User | null = authUser
    ? {
        email: authUser.email,
        name: authUser.name,
        picture: authUser.picture ?? undefined,
        roles: authUser.roles,
        isAdmin: authUser.isAdmin,
        isAppOwner: authUser.isAppOwner,
        isDeveloper: authUser.isDeveloper,
        appAccess: authUser.appAccess,
      }
    : null;

  return (
    <BrandContext.Provider
      value={{
        brands,
        selectedBrand,
        selectedBrandTenantId,
        activeTenantId,
        bootstrapTenant,
        selectBrand,
        refreshBrands,
        loading,
        error,
        user,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}

/**
 * Spec 247 — the brand context, or null outside BrandProvider.
 *
 * For a page that renders BOTH inside the app and signed out (the tour, from
 * the sign-in sheet), and reads the brand only optionally. `useBrand` throws
 * outside its provider, which is right for every page that needs a brand.
 */
export function useOptionalBrand(): BrandContextValue | null {
  return useContext(BrandContext);
}

export function useBrand(): BrandContextValue {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}
